import {
  Component,
  DestroyRef,
  ElementRef,
  AfterViewInit,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, debounceTime } from 'rxjs';
import { NgpToolbar, injectToolbarState } from 'ng-primitives/toolbar';

/**
 * Layout shell for a row of controls — the declarative replacement for the old
 * config-driven toolbar. Consumers project real controls (`sui-button`,
 * `app-search-input`, `sui-menu`, …) as content; this component owns layout +
 * responsive collapse.
 *
 * Built on `NgpToolbar` (contributes `role="toolbar"` + roving-focus keyboard
 * nav). Roving focus is item-registration based, so it stays inert until
 * projected controls opt in as `ngpRovingFocusItem`.
 *
 * **Collapse:** a `ResizeObserver` on the toolbar's own box drives `collapsed()`
 * — it flips true once content overflows (`scrollWidth > clientWidth`), caching
 * the natural width and expanding again only once the box is that wide (hysteresis,
 * or it flip-flops). It observes the box, not the children, so a control
 * iconifying can't feed back into the measurement. The taxonomy (which controls
 * iconify, which stay labelled) lives in the *consumer* — it binds
 * `[iconOnly]="tb.collapsed()"` on the controls it wants to iconify.
 *
 * Surface chrome (background, shadow, radius) is intentionally NOT baked in.
 */
@Component({
  selector: 'sui-toolbar',
  standalone: true,
  hostDirectives: [
    { directive: NgpToolbar, inputs: ['ngpToolbarOrientation: orientation'] },
  ],
  templateUrl: './sui-toolbar.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sui-toolbar.component.scss',
})
export class SuiToolbarComponent implements AfterViewInit {
  private readonly toolbar = injectToolbarState();
  private host = inject(ElementRef<HTMLElement>);
  private destroyRef = inject(DestroyRef);

  /**
   * `true` once the toolbar's controls no longer fit. Consumers bind
   * `[iconOnly]="tb.collapsed()"` on the controls they choose to iconify.
   */
  readonly collapsed = signal(false);

  private resize$ = new Subject<void>();
  private resizeObserver?: ResizeObserver;
  /** Natural (expanded) content width, cached at the moment we collapse. */
  private expandedWidth: number | null = null;

  constructor() {
    // Default to a horizontal bar (roving-focus arrow-nav = left/right). The
    // aliased `orientation` input still wins if a consumer binds it.
    this.toolbar().orientation.set('horizontal');
  }

  ngAfterViewInit(): void {
    // Observe the toolbar's own box (not its children) so iconifying can't feed
    // back into a measurement loop. Debounced to avoid thrashing on drag-resize.
    this.resize$
      .pipe(debounceTime(80), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.evaluate());

    this.resizeObserver = new ResizeObserver(() => this.resize$.next());
    this.resizeObserver.observe(this.host.nativeElement);
    this.destroyRef.onDestroy(() => this.resizeObserver?.disconnect());

    this.resize$.next(); // initial measurement
  }

  /** Collapse on overflow (caching natural width); expand only once the box is that wide again — else it oscillates. */
  private evaluate(): void {
    const el = this.host.nativeElement;
    const available = el.clientWidth;
    if (available === 0) return; // not laid out / hidden

    if (!this.collapsed()) {
      if (el.scrollWidth > available) {
        this.expandedWidth = el.scrollWidth;
        this.collapsed.set(true);
      }
    } else if (this.expandedWidth !== null && available >= this.expandedWidth) {
      this.collapsed.set(false);
      this.expandedWidth = null;
    }
  }
}
